import api from './api';

export interface Project {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  key: string;
  color?: string;
  ownerId: string;
  ownerName: string;
  startDate?: string;
  endDate?: string;
  status: string;
  taskCount: number;
  memberCount: number;
  teamCount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface ProjectMember {
  id: string;
  userId: string;
  userName: string;
  role: string;
  joinedAt: string;
}

export interface ProjectTeam {
  id: string;
  projectId: string;
  teamId: string;
  teamName: string;
  teamDescription?: string;
  memberCount: number;
  assignedAt: string;
}

export interface CreateProjectDto {
  name: string;
  description?: string;
  key: string;
  color?: string;
  startDate?: string;
  endDate?: string;
}

export interface UpdateProjectDto {
  name?: string;
  description?: string;
  color?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

export interface AddProjectMemberDto {
  userId: string;
  roleId: string;
}

const projectService = {
  // Get all projects
  getProjects: async (): Promise<Project[]> => {
    const response = await api.get<Project[]>('/projects');
    return response.data;
  },

  // Get project by ID
  getProject: async (id: string): Promise<Project> => {
    const response = await api.get<Project>(`/projects/${id}`);
    return response.data;
  },

  // Create new project
  createProject: async (data: CreateProjectDto): Promise<Project> => {
    const response = await api.post<Project>('/projects', data);
    return response.data;
  },

  // Update project
  updateProject: async (id: string, data: UpdateProjectDto): Promise<Project> => {
    const response = await api.put<Project>(`/projects/${id}`, data);
    return response.data;
  },

  // Delete project
  deleteProject: async (id: string): Promise<void> => {
    await api.delete(`/projects/${id}`);
  },

  // Get project members
  getProjectMembers: async (projectId: string): Promise<ProjectMember[]> => {
    const response = await api.get<ProjectMember[]>(`/projects/${projectId}/members`);
    return response.data;
  },

  // Add project member
  addProjectMember: async (projectId: string, data: AddProjectMemberDto): Promise<ProjectMember> => {
    const response = await api.post<ProjectMember>(`/projects/${projectId}/members`, data);
    return response.data;
  },

  // Remove project member
  removeProjectMember: async (projectId: string, userId: string): Promise<void> => {
    await api.delete(`/projects/${projectId}/members/${userId}`);
  },

  // Get project teams
  getProjectTeams: async (projectId: string): Promise<ProjectTeam[]> => {
    const response = await api.get<ProjectTeam[]>(`/projects/${projectId}/teams`);
    return response.data;
  },

  // Assign team to project
  assignTeamToProject: async (projectId: string, teamId: string): Promise<ProjectTeam> => {
    const response = await api.post<ProjectTeam>(`/projects/${projectId}/teams`, { teamId });
    return response.data;
  },

  // Unassign team from project
  unassignTeamFromProject: async (projectId: string, teamId: string): Promise<void> => {
    await api.delete(`/projects/${projectId}/teams/${teamId}`);
  },
};

export default projectService;
